import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Download, Image as ImageIcon, Images } from "lucide-react";
import { toast } from "sonner";
import { PalpitesData } from "./palpitesGenerator";
import {
  buildImagemSlides,
  renderImagemSlide,
  baixarImagemBlob,
} from "./imagemRenderer";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  data: PalpitesData;
}

const GerarImagemModal: React.FC<Props> = ({ open, onOpenChange, data }) => {
  const slides = useMemo(() => buildImagemSlides(data), [data]);
  const [slideId, setSlideId] = useState<string>("capa");
  const [loading, setLoading] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);

  const stamp = () => data.data.replace(/\//g, "-");

  const handleUm = async () => {
    setLoading(true);
    try {
      const slide = slides.find((s) => s.id === slideId) ?? slides[0];
      const blob = await renderImagemSlide(slide);
      baixarImagemBlob(blob, `palpite-guru-${slide.id}-${stamp()}.png`);
      toast.success("Imagem 16:9 baixada!");
    } catch (e) {
      console.error(e);
      toast.error("Erro ao gerar imagem.");
    } finally {
      setLoading(false);
    }
  };

  const handleTodos = async () => {
    setLoadingAll(true);
    try {
      for (const slide of slides) {
        const blob = await renderImagemSlide(slide);
        baixarImagemBlob(blob, `palpite-guru-${slide.id}-${stamp()}.png`);
        await new Promise((r) => setTimeout(r, 500));
      }
      toast.success(`${slides.length} imagens baixadas!`);
    } catch (e) {
      console.error(e);
      toast.error("Erro ao gerar imagens.");
    } finally {
      setLoadingAll(false);
    }
  };

  const busy = loading || loadingAll;

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-yellow-500" />
            Gerar Imagem 16:9 (Capa YouTube)
          </DialogTitle>
          <DialogDescription>
            PNG 1920×1080 gerado no seu navegador. <b>Zero créditos de IA.</b> Fotos dos bichos
            sempre coerentes com os grupos gerados.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label>Slide</Label>
            <Select value={slideId} onValueChange={setSlideId} disabled={busy}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {slides.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button
            variant="outline"
            onClick={handleTodos}
            disabled={busy}
            className="border-yellow-500 text-yellow-600 hover:bg-yellow-500/10"
          >
            {loadingAll ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gerando…</>
            ) : (
              <><Images className="w-4 h-4 mr-2" /> Baixar todas ({slides.length})</>
            )}
          </Button>
          <Button
            onClick={handleUm}
            disabled={busy}
            className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gerando…</>
            ) : (
              <><Download className="w-4 h-4 mr-2" /> Baixar PNG 16:9</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GerarImagemModal;
